import { restoreAttribute } from '../core/utils';
import { Component, BaseOptions } from '../core/component';

export interface CarouselI18nOptions {
  carousel: string;
  item: string;
  of: string;
  indicators: string;
  slide: string;
}

export interface CarouselOptions extends BaseOptions {
  /**
   * Milliseconds allowed for a programmatic scroll to land. The track itself
   * scrolls at the browser's own smooth-scroll speed; this only bounds how
   * long the component waits for `scrollend` before assuming it arrived, and
   * is added to `interval` to time each auto-advance rest.
   * @default 200
   */
  duration: number;
  /**
   * Show paging dots. Only drawn when there is more than one item.
   * @default false
   */
  indicators: boolean;
  /**
   * Stop auto-advance at the last item instead of looping back to the first.
   * A scroll track never wraps under arrow keys or `set()` either way.
   * @default false
   */
  noWrap: boolean;
  /**
   * Milliseconds to rest between automatic advances, on top of `duration` -
   * the gap follows each transition rather than containing it, so a full cycle
   * takes `duration + interval`. 0 leaves auto-advance off.
   * Whenever it is set the track pauses on hover and on keyboard focus, and
   * `prefers-reduced-motion: reduce` suppresses it entirely - auto-advancing
   * content is WCAG 2.2.2 territory, so neither is an option an author can
   * turn off. `noWrap` stops it after one pass instead of looping.
   * @default 0
   */
  interval: number;
  /**
   * Fixed track height in pixels. `null` sizes the carousel from its content.
   * A fixed height gives the indicators their own row below the track instead
   * of laying them over the media.
   * @default null
   */
  height: number | null;
  /**
   * Called when a new item becomes the center.
   * @default null
   */
  onCycleTo: (current: Element, dragged: boolean) => void;
  /**
   * Accessible names generated for an unlabeled carousel, its items and its
   * indicators.
   */
  i18n: Partial<CarouselI18nOptions>;
}

const _defaults: CarouselOptions = {
  duration: 200,
  indicators: false,
  noWrap: false,
  interval: 0,
  height: null,
  onCycleTo: null,
  i18n: {
    carousel: 'Carousel',
    item: 'Item',
    of: 'of',
    indicators: 'Slides',
    slide: 'Slide'
  }
};

const naturallyFocusable =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export class Carousel extends Component<CarouselOptions> {
  hasMultipleSlides: boolean;
  showIndicators: boolean;
  pressed: boolean;
  dragged: boolean;
  images: HTMLElement[];
  _indicators: HTMLUListElement | HTMLElement;
  count: number;
  center: number = 0;
  scrollingTimeout: ReturnType<typeof setTimeout>;
  oneTimeCallback: (current: Element, dragged: boolean) => void | null;

  private _ownIndicators: boolean = false;
  private _trackEl: HTMLElement | null = null;
  private _ignoreScroll: boolean = false;
  private _alignAfterResize = false;
  private _scrollEndTimeout: ReturnType<typeof setTimeout>;
  private _trackRaf: number = null;
  private _started: boolean = false;
  private _vertical: boolean = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _resizeTimeout: ReturnType<typeof setTimeout> = null;
  private _dragPointerId: number | null = null;
  private _dragStartX: number = 0;
  private _dragStartY: number = 0;
  private _dragStartScroll: number = 0;
  private _suppressClick: boolean = false;
  private _suppressClickTimeout: ReturnType<typeof setTimeout> = null;
  private _generatedContainerRole: boolean = false;
  private _generatedContainerLabel: boolean = false;
  private _generatedContainerDescription: boolean = false;
  private _generatedWideLayout: boolean = false;
  private _generatedFixedHeight: boolean = false;
  private _authoredInlineHeight: string | null = null;
  private _autoAdvanceTimer: ReturnType<typeof setTimeout> = null;
  private _autoAdvances: boolean = false;
  private _autoPaused: boolean = false;
  private _hovered: boolean = false;
  private _focused: boolean = false;
  private _motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  private _generatedItemTabIndexes = new Set<HTMLElement>();
  private _generatedItemLabels = new Set<HTMLElement>();
  private _generatedItemDescriptions = new Set<HTMLElement>();
  private _generatedItemSizes = new Set<HTMLElement>();
  private _authoredItemSizes = new Set<HTMLElement>();
  private _itemA11yOriginal = new Map<
    HTMLElement,
    { ariaHidden: string | null; tabIndex: string | null }
  >();

  private get _scroller(): HTMLElement {
    return this._trackEl ?? this.el;
  }

  constructor(el: HTMLElement, options: Partial<CarouselOptions>) {
    super(el, options);

    this._vertical = this.el.classList.contains('full-screen');

    this.pressed = false;
    this.dragged = false;
    this.images = this._collectItems();
    if (!this.images.length) {
      console.error('Carousel: no .carousel-item elements to show');
      return;
    }
    this.images.forEach((item) => {
      this._itemA11yOriginal.set(item, {
        ariaHidden: item.getAttribute('aria-hidden'),
        tabIndex: item.getAttribute('tabindex')
      });
      if (item.hasAttribute('data-carousel-size')) this._authoredItemSizes.add(item);
    });

    this.hasMultipleSlides = this.images.length > 1;
    this.showIndicators = this.options.indicators && this.hasMultipleSlides;
    this.count = this.images.length;

    this._autoAdvances = this.options.interval > 0 && this.hasMultipleSlides;

    this.images.forEach((item) => item.classList.add('carousel-item'));
    if (this.options.height !== null) {
      this._generatedFixedHeight = !this.el.classList.contains('fixed-height');
      this._authoredInlineHeight = this.el.style.getPropertyValue('--carousel-height') || null;
      this.el.classList.add('fixed-height');
      this.el.style.setProperty('--carousel-height', `${this.options.height}px`);
    }
    this._wrapTrack();
    this._syncAdaptiveMode();

    if (this.showIndicators) this._setupIndicators();

    this._setupAccessibility();

    this._setupEventHandlers();
    this._started = true;

    const start = Math.max(
      0,
      this.images.findIndex((el) => el.classList.contains('active'))
    );
    this.center = start;
    this._syncActive(start, false);
    this._updateParallax();
    this._scrollToIndex(start, false);

    this._syncAutoAdvance();
  }

  static get defaults(): CarouselOptions {
    return _defaults;
  }

  static key = 'Expressive_Carousel';

  destroy() {
    if (this._started) this._teardown();
    super.destroy();
  }

  private _collectItems(): HTMLElement[] {
    const explicit = Array.from(this.el.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && el.classList.contains('carousel-item')
    );
    if (explicit.length) return explicit;
    return Array.from(this.el.children).filter((el): el is HTMLElement => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.classList.contains('indicators')) {
        return false;
      }
      return el.tagName !== 'NAV';
    });
  }

  private _setupAccessibility() {
    if (!this.el.hasAttribute('role')) {
      this.el.setAttribute('role', 'region');
      this._generatedContainerRole = true;
    }
    if (!this.el.hasAttribute('aria-label') && !this.el.hasAttribute('aria-labelledby')) {
      this.el.setAttribute('aria-label', this.options.i18n.carousel);
      this._generatedContainerLabel = true;
    }
    if (!this.el.hasAttribute('aria-roledescription')) {
      this.el.setAttribute('aria-roledescription', 'carousel');
      this._generatedContainerDescription = true;
    }

    this.images.forEach((item, index) => {
      if (!item.hasAttribute('aria-roledescription')) {
        item.setAttribute('aria-roledescription', 'slide');
        this._generatedItemDescriptions.add(item);
      }

      if (!item.hasAttribute('aria-label') && !item.hasAttribute('aria-labelledby')) {
        const mediaName = item.querySelector('img[alt]')?.getAttribute('alt')?.trim();
        const textName = item.textContent?.replace(/\s+/g, ' ').trim();
        const name = mediaName || textName;
        const position = `${this.options.i18n.item} ${index + 1} ${this.options.i18n.of} ${this.count}`;
        item.setAttribute('aria-label', name ? `${name}, ${position}` : position);
        this._generatedItemLabels.add(item);
      }

      if (!item.matches(naturallyFocusable) && !item.querySelector(naturallyFocusable)) {
        item.tabIndex = 0;
        this._generatedItemTabIndexes.add(item);
      }
    });
  }

  private _setupIndicators() {
    const existing = Array.from(this.el.children).find(
      (el): el is HTMLElement => el instanceof HTMLElement && el.classList.contains('indicators')
    );
    this._indicators = existing ?? document.createElement('nav');
    this._ownIndicators = !existing;
    this._indicators.classList.add('indicators');
    this._indicators.setAttribute('aria-label', this.options.i18n.indicators);
    this._indicators.replaceChildren();

    this.images.forEach((_, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.add('indicator-item');
      button.setAttribute('aria-label', `${this.options.i18n.slide} ${i + 1}`);
      this._indicators.appendChild(button);
    });

    if (this._ownIndicators) this.el.appendChild(this._indicators);
  }

  _setupEventHandlers() {
    const { signal } = this._events;
    this.el.addEventListener('keydown', this._handleKeydown, { signal });
    this._motion.addEventListener('change', this._handleMotionChange, { signal });
    if (this._autoAdvances) {
      this.el.addEventListener('mouseenter', this._handleAutoAdvanceState, { signal });
      this.el.addEventListener('mouseleave', this._handleAutoAdvanceState, { signal });
      this.el.addEventListener('focusin', this._handleAutoAdvanceState, { signal });
      this.el.addEventListener('focusout', this._handleAutoAdvanceState, { signal });
      // Browsers clamp a background tab's timers rather than stopping them, so
      // without this the track keeps cycling out of sight.
      document.addEventListener('visibilitychange', this._handleAutoAdvanceState, { signal });
    }
    if (this.showIndicators && this._indicators) {
      this._indicators.addEventListener('click', this._handleIndicatorClick, { signal });
    }
    this._resizeObserver = new ResizeObserver(() => this._handleThrottledResize());
    this._resizeObserver.observe(this.el);

    // Full-screen needs a window signal when orientation changes without
    // changing the container width. ResizeObserver handles every other track,
    // including pane resizes.
    if (this.el.classList.contains('full-screen')) {
      window.addEventListener('resize', this._handleThrottledResize, { passive: true, signal });
    }

    this._scroller.addEventListener('scroll', this._handleTrackScroll, { passive: true, signal });
    this._scroller.addEventListener('pointerdown', this._handleTrackPointerDown, { signal });
    this._scroller.addEventListener('wheel', this._handleNativeScroll, { passive: true, signal });
    this._scroller.addEventListener('click', this._handleTrackClick, { capture: true, signal });
    this._scroller.addEventListener('dragstart', this._handleTrackDragStart, { signal });
    this._scroller.addEventListener('transitionend', this._handleItemResize, { signal });
  }

  private _teardown() {
    this._started = false;
    this._events.abort();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._finishScroll();
    this._autoAdvances = false;
    this._syncAutoAdvance();
    if (this._trackRaf !== null) cancelAnimationFrame(this._trackRaf);
    window.clearTimeout(this.scrollingTimeout);
    window.clearTimeout(this._suppressClickTimeout);
    clearTimeout(this._resizeTimeout);
    if (this._ownIndicators) this._indicators?.remove();
    this._unwrapTrack();
    this.images.forEach((el) => {
      el.style.removeProperty('--md-comp-carousel-item-parallax');
      if (this._generatedItemLabels.has(el)) el.removeAttribute('aria-label');
      if (this._generatedItemDescriptions.has(el)) el.removeAttribute('aria-roledescription');
      if (this._generatedItemSizes.has(el)) el.removeAttribute('data-carousel-size');
      const original = this._itemA11yOriginal.get(el);
      if (original) {
        restoreAttribute(el, 'aria-hidden', original.ariaHidden);
        restoreAttribute(el, 'tabindex', original.tabIndex);
      }
    });
    if (this._generatedContainerRole) this.el.removeAttribute('role');
    if (this._generatedContainerLabel) this.el.removeAttribute('aria-label');
    if (this._generatedContainerDescription) this.el.removeAttribute('aria-roledescription');
    if (this._generatedFixedHeight) this.el.classList.remove('fixed-height');
    if (this.options.height !== null) {
      if (this._authoredInlineHeight === null) this.el.style.removeProperty('--carousel-height');
      else this.el.style.setProperty('--carousel-height', this._authoredInlineHeight);
    }
    if (this._generatedWideLayout) {
      this.el.classList.remove(
        'multi-wide',
        'multi-large',
        'multi-extra-large',
        'full-screen-horizontal'
      );
    }
  }

  _handleThrottledResize = () => {
    if (!this._started || this._resizeTimeout !== null) return;
    this._handleResize();
    this._resizeTimeout = setTimeout(() => {
      this._resizeTimeout = null;
      this._handleResize();
    }, 200);
  };

  _handleAutoAdvanceState = (e: Event) => {
    if (e.type === 'mouseenter') this._hovered = true;
    else if (e.type === 'mouseleave') this._hovered = false;
    else if (e.type === 'focusin') this._focused = true;
    else if (e.type === 'focusout') this._focused = this.el.contains((e as FocusEvent).relatedTarget as Node);
    this._syncAutoAdvance();
  };

  private _handleMotionChange = () => {
    this._syncAutoAdvance();
    if (this._motion.matches) this._scrollToIndex(this.center, false);
    this._updateParallax();
  };

  /** Start or stop the timer to match the current pause state. */
  private _syncAutoAdvance() {
    const run =
      this._autoAdvances &&
      !this._autoPaused &&
      !this._hovered &&
      !this._focused &&
      !this._motion.matches &&
      !document.hidden;
    if (run && this._autoAdvanceTimer === null) {
      // Each rest is armed after the move before it, never on a fixed phase.
      // A repeating timer assumes the work it triggers is instant; a smooth
      // scroll is not, so a fixed period either lands mid-scroll or, once a
      // tick is dropped, leaves whatever is left of the period as the next
      // rest.
      this._autoAdvanceTimer = setTimeout(
        this._tick,
        this.options.duration + this.options.interval
      );
    } else if (!run && this._autoAdvanceTimer !== null) {
      clearTimeout(this._autoAdvanceTimer);
      this._autoAdvanceTimer = null;
    }
  }

  private _tick = () => {
    this._autoAdvanceTimer = null;
    this._advance();
    this._syncAutoAdvance();
  };

  private _advance = () => {
    const next = this.center + 1;
    if (next < this.count) {
      this.set(next);
      return;
    }
    // Arrow keys and `set()` never wrap, because a scroll container has ends.
    // Auto-advance is the one caller that can: looping back to the first item
    // is just a scroll. An author who asked for no wrapping gets one pass and
    // then silence.
    if (this.options.noWrap) {
      this._autoAdvances = false;
      this._syncAutoAdvance();
      return;
    }
    this.set(0);
  };

  private _handleNativeScroll = () => {
    this._alignAfterResize = false;
    this._finishScroll();
  };

  /** The document only hears from a carousel while a mouse press is down on its track. */
  private _setDragListeners(on: boolean) {
    const method = on ? 'addEventListener' : 'removeEventListener';
    // The signal also removes them when the carousel is destroyed mid-drag.
    const options = { signal: this._events.signal };
    document[method]('pointermove', this._handleTrackPointerMove, options);
    document[method]('pointerup', this._handleTrackPointerUp, options);
    document[method]('pointercancel', this._handleTrackPointerUp, options);
  }

  _handleTrackPointerDown = (e: PointerEvent) => {
    this._handleNativeScroll();
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    this.pressed = true;
    this.dragged = false;
    this._suppressClick = false;
    this._dragPointerId = e.pointerId;
    this._dragStartX = e.clientX;
    this._dragStartY = e.clientY;
    this._dragStartScroll = this._vertical ? this._scroller.scrollTop : this._scroller.scrollLeft;
    this._setDragListeners(true);
  };

  _handleTrackPointerMove = (e: PointerEvent) => {
    if (!this.pressed || e.pointerId !== this._dragPointerId) return;
    const deltaX = e.clientX - this._dragStartX;
    const deltaY = e.clientY - this._dragStartY;
    const primaryDelta = this._vertical ? deltaY : deltaX;
    const crossDelta = this._vertical ? deltaX : deltaY;

    if (!this.dragged) {
      if (Math.abs(primaryDelta) < 6 || Math.abs(primaryDelta) <= Math.abs(crossDelta)) return;
      this.dragged = true;
      this.el.classList.add('dragging');
      try {
        this._scroller.setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture is optional in older browsers and test DOMs.
      }
    }

    if (this._vertical) this._scroller.scrollTop = this._dragStartScroll - deltaY;
    else this._scroller.scrollLeft = this._dragStartScroll - deltaX;
    e.preventDefault();
  };

  _handleTrackPointerUp = (e: PointerEvent) => {
    if (!this.pressed || e.pointerId !== this._dragPointerId) return;
    this.pressed = false;
    this._dragPointerId = null;
    this._setDragListeners(false);
    this.el.classList.remove('dragging');
    try {
      this._scroller.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer capture may already have been released.
    }

    if (!this.dragged) return;
    this._suppressClick = true;
    window.clearTimeout(this._suppressClickTimeout);
    this._suppressClickTimeout = setTimeout(() => {
      this._suppressClick = false;
    }, 0);
    this._syncActive(this._nearestIndex(), true);
  };

  _handleTrackClick = (e: MouseEvent) => {
    if (!this._suppressClick) return;
    this._suppressClick = false;
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  _handleTrackDragStart = (e: DragEvent) => {
    if ((e.target as HTMLElement).closest('a, img, picture, video')) e.preventDefault();
  };

  private _handleItemResize = (e: TransitionEvent) => {
    if (this._alignAfterResize && !this.pressed && e.propertyName === 'flex-basis' && this.images.includes(e.target as HTMLElement)) {
      this._scrollToIndex(this.center, false);
    }
  };

  _handleIndicatorClick = (e: Event) => {
    e.stopPropagation();
    const indicator = (e.target as HTMLElement).closest('.indicator-item');
    if (!indicator || !this._indicators.contains(indicator)) return;
    const index = [...this._indicators.children].indexOf(indicator);
    if (index < 0) return;
    this._cycleTo(index);
  };

  /**
   * Keys the carousel leaves alone. Modified keys are shortcuts (Alt+Left is
   * Back), and a nested widget that reads arrows itself - a field, a slider,
   * tabs, a menu, media controls - keeps them.
   */
  private _keyBelongsElsewhere(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    return [e.defaultPrevented, e.altKey, e.ctrlKey, e.metaKey, e.shiftKey, target.isContentEditable].some(Boolean) ||
      this.el.contains(target.closest(
        'input, textarea, select, video[controls], audio[controls], [role="slider"], [role="tablist"], [role="menu"]'
      ));
  }

  _handleKeydown = (e: KeyboardEvent) => {
    if (this._keyBelongsElsewhere(e)) return;
    const rtl = getComputedStyle(this._scroller).direction === 'rtl';
    const forward = this._vertical ? e.key === 'ArrowDown' : e.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
    const backward = this._vertical ? e.key === 'ArrowUp' : e.key === (rtl ? 'ArrowRight' : 'ArrowLeft');
    if (e.key === 'Home') {
      e.preventDefault();
      this.set(0);
      this._focusItem(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.set(this.count - 1);
      this._focusItem(this.count - 1);
    } else if (forward) {
      e.preventDefault();
      this.next();
      this._focusItem(this.center);
    } else if (backward) {
      e.preventDefault();
      this.prev();
      this._focusItem(this.center);
    }
  };

  _handleTrackScroll = () => {
    if (!this.el.classList.contains('scrolling')) this.el.classList.add('scrolling');
    window.clearTimeout(this.scrollingTimeout);
    this.scrollingTimeout = setTimeout(() => this.el.classList.remove('scrolling'), 120);
    if (this._trackRaf !== null) return;
    this._trackRaf = requestAnimationFrame(() => {
      this._trackRaf = null;
      // Read the nearest index before the parallax writes invalidate style.
      const index = this._ignoreScroll ? this.center : this._nearestIndex();
      this._updateParallax();
      if (index !== this.center) this._syncActive(index, this.dragged);
    });
  };

  _handleResize = () => {
    if (!this._started || !this.images.length) return;
    this._syncAdaptiveMode();
    this._syncLayoutRoles(this.center);
    if (!this.pressed) this._scrollToIndex(this.center, false);
    this._updateParallax();
  };

  private _paintIndicators(center: number) {
    if (!this.showIndicators || !this._indicators) return;
    const pos = ((center % this.count) + this.count) % this.count;
    this._indicators.querySelectorAll('.indicator-item').forEach((el, i) => {
      const on = i === pos;
      el.classList.toggle('active', on);
      if (on) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }

  /** Whether `index` is still the active item once its change is reported. */
  private _syncActive(index: number, dragged: boolean): boolean {
    const prev = this.center;
    this.center = index;
    this.images.forEach((el, i) => el.classList.toggle('active', i === index));
    this._paintIndicators(index);
    this._syncLayoutRoles(index);
    const curr = this.images[index];
    const detail = { item: curr, dragged };
    if (prev !== index && !this._notify('change', this.options.onCycleTo, { args: [curr, dragged], detail, current: () => this.center === index })) return false;
    if (typeof this.oneTimeCallback === 'function') {
      this.oneTimeCallback.call(this, curr, dragged);
      this.oneTimeCallback = null;
    }
    return true;
  }

  private _syncLayoutRoles(index: number) {
    // Every size is worked out before any is written. Writing `large` to all
    // items and then reading clientWidth forced a layout in that interim
    // state on every center change.
    type Size = 'large' | 'medium' | 'small';
    const sizes: Size[] = this.images.map(() => 'large');
    const setSize = (i: number, size: Size) => {
      if (this.images[i]) sizes[i] = size;
    };

    if (
      this.el.classList.contains('uncontained') ||
      (this.el.classList.contains('full-screen') && this._vertical)
    ) {
      // Every item stays large.
    } else if (
      this.el.classList.contains('hero') ||
      this.el.classList.contains('full-screen-horizontal')
    ) {
      setSize(index + 1, 'small');
      if (this.el.classList.contains('center-aligned')) setSize(index - 1, 'small');
    } else {
      const width = this.el.clientWidth;
      const desiredLargeCount = width >= 1600 ? 4 : width >= 1200 ? 3 : width >= 600 ? 2 : 1;
      const largeCount = Math.min(desiredLargeCount, Math.max(1, this.count - 2));
      this.el.classList.remove('multi-wide', 'multi-large', 'multi-extra-large');
      if (largeCount === 2) this.el.classList.add('multi-wide');
      else if (largeCount === 3) this.el.classList.add('multi-large');
      else if (largeCount >= 4) this.el.classList.add('multi-extra-large');
      this._generatedWideLayout = this._generatedWideLayout || largeCount > 1;
      setSize(index + largeCount, 'medium');
      setSize(index + largeCount + 1, 'small');
    }

    this.images.forEach((item, i) => {
      if (this._authoredItemSizes.has(item)) return;
      item.setAttribute('data-carousel-size', sizes[i]);
      this._generatedItemSizes.add(item);
    });
  }

  private _syncAdaptiveMode() {
    if (!this.el.classList.contains('full-screen')) return;
    const landscape = window.matchMedia('(orientation: landscape)').matches;
    const horizontal = this.el.clientWidth >= 840 || landscape;
    const wasVertical = this._vertical;
    this._vertical = !horizontal;
    this.el.classList.toggle('full-screen-horizontal', horizontal);
    this._generatedWideLayout = this._generatedWideLayout || horizontal;

    if (this._started && wasVertical !== this._vertical) {
      this._scroller.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    }
  }

  private _focusItem(index: number) {
    const item = this.images[index];
    if (!item) return;
    const focusable = item.matches(naturallyFocusable)
      ? item
      : item.querySelector<HTMLElement>(naturallyFocusable);
    (focusable ?? item).focus({ preventScroll: true });
  }

  private _updateParallax() {
    if (this._motion.matches) {
      this.images.forEach((item) =>
        item.style.setProperty('--md-comp-carousel-item-parallax', '0px')
      );
      return;
    }

    const scrollerRect = this._scroller.getBoundingClientRect();
    const viewportCenter = this._vertical
      ? scrollerRect.top + scrollerRect.height / 2
      : scrollerRect.left + scrollerRect.width / 2;
    const viewportSize = Math.max(1, this._vertical ? scrollerRect.height : scrollerRect.width);

    // All reads, then all writes: a write between two reads forces a style
    // recalculation per item on every scroll frame.
    const rects = this.images.map((item) => item.getBoundingClientRect());
    this.images.forEach((item, i) => {
      const rect = rects[i];
      const itemCenter = this._vertical ? rect.top + rect.height / 2 : rect.left + rect.width / 2;
      const progress = Math.max(-1, Math.min(1, (itemCenter - viewportCenter) / viewportSize));
      item.style.setProperty('--md-comp-carousel-item-parallax', `${progress * -24}px`);
    });
  }

  private _wrapTrack() {
    const track = document.createElement('div');
    track.className = 'carousel-track';
    this.images[0].before(track);
    this.images.forEach((el) => track.appendChild(el));
    this._trackEl = track;
  }

  private _unwrapTrack() {
    if (!this._trackEl) return;
    this.images.forEach((el) => this.el.insertBefore(el, this._trackEl));
    this._trackEl.remove();
    this._trackEl = null;
  }

  private _nearestIndex(): number {
    const centered = this.el.classList.contains('center-aligned');
    const style = getComputedStyle(this._scroller);
    const rtl = !this._vertical && style.direction === 'rtl';
    const position = this._vertical ? this._scroller.scrollTop : this._scroller.scrollLeft;
    const viewport = this._vertical ? this._scroller.clientHeight : this._scroller.clientWidth;
    const extent = (this._vertical ? this._scroller.scrollHeight : this._scroller.scrollWidth) - viewport;
    const padding = parseFloat(this._vertical ? style.paddingBlockStart : style.paddingInlineStart) || 0;
    let best = this.center;
    let bestDist = Infinity;
    let centerDist = Infinity;
    this.images.forEach((el, i) => {
      const start = this._vertical ? el.offsetTop : el.offsetLeft;
      const size = this._vertical ? el.offsetHeight : el.offsetWidth;
      const target = centered ? start - (viewport - size) / 2 : start + (rtl ? size - viewport + padding : -padding);
      const bounded = Math.max(rtl ? -extent : 0, Math.min(rtl ? 0 : extent, target));
      const dist = Math.abs(bounded - position);
      if (i === this.center) centerDist = dist;
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    // Items past the scroll end all clamp to it, and a subpixel wobble there
    // (WebKit reports -179 for -178 in a right-to-left track) must not hand
    // the selection to a later item that only shares the clamped position.
    return centerDist - bestDist <= 1 ? this.center : best;
  }

  private _scrollToIndex(n: number, smooth = true) {
    const el = this.images[n];
    if (!el) return;
    this._finishScroll();
    const centered = this.el.classList.contains('center-aligned');
    const style = getComputedStyle(this._scroller);
    const inlinePadding = Number.parseFloat(style.paddingInlineStart) || 0;
    const blockPadding = Number.parseFloat(style.paddingBlockStart) || 0;
    const left = centered
      ? el.offsetLeft - (this._scroller.clientWidth - el.offsetWidth) / 2
      : style.direction === 'rtl'
        ? el.offsetLeft + el.offsetWidth - this._scroller.clientWidth + inlinePadding
        : el.offsetLeft - inlinePadding;
    const top = el.offsetTop - blockPadding;
    this._ignoreScroll = true;
    // Item sizes are still transitioning, so the track can be too narrow to
    // reach the target and clamps short of it. Firefox takes whatever item
    // sits at that clamped position as the snap target and re-snaps to it
    // after each later layout change, undoing the alignment below. Snapping
    // returns in _finishScroll, once the track has settled on the item.
    this._scroller.style.scrollSnapType = 'none';
    this._scroller.scrollTo({
      left: this._vertical ? 0 : left,
      top: this._vertical ? top : 0,
      behavior: smooth && !this._motion.matches ? 'smooth' : 'instant'
    });
    this._scroller.addEventListener('scrollend', this._handleScrollEnd);
    this._scrollEndTimeout = setTimeout(this._finishScroll, this.options.duration + 350);
  }

  private _finishScroll = () => {
    this._ignoreScroll = false;
    this._scroller.style.scrollSnapType = '';
    clearTimeout(this._scrollEndTimeout);
    this._scroller.removeEventListener('scrollend', this._handleScrollEnd);
  };

  private _handleScrollEnd = () => {
    // An earlier scroll can finish after a new navigation has started.
    if (this._nearestIndex() === this.center && !this.images.some(item => item.getAnimations().some(animation => animation.playState === 'running'))) {
      this._finishScroll();
    }
  };

  _cycleTo(n: number, callback: CarouselOptions['onCycleTo'] = null) {
    this._alignAfterResize = true;
    if (typeof callback === 'function') this.oneTimeCallback = callback;
    // A listener that moved to another item, or destroyed the carousel, has
    // already scrolled where it wanted.
    if (this._syncActive(n, false)) this._scrollToIndex(n);
  }

  /**
   * Stop auto-advance until start() is called. Without an `interval` there is
   * nothing to stop and this does nothing.
   */
  pause() {
    this._autoPaused = true;
    this._syncAutoAdvance();
  }

  /** Resume auto-advance after pause(). */
  start() {
    this._autoPaused = false;
    this._syncAutoAdvance();
  }

  next(n: number = 1) {
    if (isNaN(n)) n = 1;
    const index = this.center + n;
    if (index >= this.count || index < 0) return;
    this._cycleTo(index);
  }

  prev(n: number = 1) {
    if (isNaN(n)) n = 1;
    const index = this.center - n;
    if (index >= this.count || index < 0) return;
    this._cycleTo(index);
  }

  set(n: number, callback?: CarouselOptions['onCycleTo']) {
    if (n === undefined || isNaN(n)) n = 0;
    if (n >= this.count || n < 0) return;
    this._cycleTo(n, callback);
  }
}
